```vue
<template>
  <div v-if="isLoadingAuth" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-70">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-500"></div>
  </div>

  <div v-else-if="isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-70">
    <div class="p-4 space-y-4 w-full max-w-2xl">
      <div v-for="i in 3" :key="i" class="bg-gray-700 h-32 rounded-md animate-pulse"></div>
    </div>
  </div>

  <div v-else-if="!user" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-70">
    <div class="text-indigo-300 text-sm p-4">กรุณาล็อกอิน</div>
  </div>

  <div v-else-if="error" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-70">
    <div class="text-red-400 text-sm p-4">{{ error }}</div>
  </div>

  <div v-else class="fixed inset-0 bg-gray-700 bg-opacity-50 flex items-center justify-center p-4 z-50 animate-modalFade">
    <div class="w-full max-w-4xl bg-gray-800 text-gray-300 rounded-lg p-5 flex flex-col gap-4 relative shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Tabs -->
      <div class="flex border-b border-gray-700 bg-gray-700 rounded-md">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          @click="activeTab = tab.id"
          class="relative px-4 py-2 text-sm font-medium transition-colors duration-200 border-b-2 group"
          :class="activeTab === tab.id
            ? 'text-indigo-300 border-indigo-500 bg-gray-600'
            : 'text-gray-300 border-transparent hover:text-indigo-300 hover:bg-gray-600'"
        >
          <span>{{ tab.name }}</span>
          <span
            v-if="tabClaimableCounts[tab.id] > 0"
            class="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-600 text-white text-xxs rounded-full w-4 h-4 flex items-center justify-center z-10"
          >
            {{ tabClaimableCounts[tab.id] }}
          </span>
          <span class="tooltip">{{ tab.tooltip }}</span>
        </button>
      </div>

      <!-- Sub Tabs for Main -->
      <div v-if="activeTab === 'main'" class="flex border-b border-gray-700 bg-gray-700 rounded-md">
        <button
          @click="activeMainSubTab = 'incomplete'"
          class="relative px-4 py-2 text-sm font-medium border-b-2 group"
          :class="activeMainSubTab === 'incomplete'
            ? 'text-indigo-300 border-indigo-500 bg-gray-600'
            : 'text-gray-300 border-transparent hover:text-indigo-300 hover:bg-gray-600'"
        >
          ยังไม่ได้รับรางวัล
          <span class="tooltip">ภารกิจที่ยังรับรางวัลไม่ได้/ยังทำไม่ครบ</span>
        </button>
        <button
          @click="activeMainSubTab = 'completed'"
          class="relative px-4 py-2 text-sm font-medium border-b-2 group"
          :class="activeMainSubTab === 'completed'
            ? 'text-indigo-300 border-indigo-500 bg-gray-600'
            : 'text-gray-300 border-transparent hover:text-indigo-300 hover:bg-gray-600'"
        >
          ได้รับรางวัลแล้ว
          <span class="tooltip">ภารกิจหลักที่รับรางวัลเรียบร้อย</span>
        </button>
      </div>

      <!-- Mission List -->
      <div class="overflow-y-auto scrollbar-thin h-72 flex-grow" id="style-1">
        <div v-if="getMissionsForTab(activeTab).length === 0" class="text-indigo-300 text-sm text-center">
          ไม่พบข้อมูล
        </div>

        <div v-else class="space-y-4">
          <!-- Main Tab -->
          <template v-if="activeTab === 'main'">
            <!-- Incomplete -->
            <template v-if="activeMainSubTab === 'incomplete'">
              <div
                v-for="mission in incompleteMain"
                :key="mission.missionId"
                class="flex flex-col p-4 bg-gray-700 rounded-md border border-gray-600 hover:border-indigo-500 transition-all duration-200"
              >
                <div class="flex justify-between items-center">
                  <div class="flex items-center gap-2">
                    <i class="fas fa-tasks text-indigo-400"></i>
                    <span class="font-medium text-indigo-200 text-base">{{ mission.name }}</span>
                  </div>
                  <span class="text-indigo-400 font-semibold text-sm">{{ missionProgress(mission) }}</span>
                </div>

                <div class="text-indigo-300 text-xs mt-2">{{ mission.description }}</div>

                <!-- Progress Bar -->
                <div class="w-full bg-gray-600 rounded-full h-2.5 mt-2 border border-gray-500">
                  <div
                    class="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                    :style="{ width: progressPercentage(mission) + '%' }"
                  ></div>
                </div>

                <!-- Rewards -->
                <div class="mt-3">
                  <span class="font-medium text-indigo-200 text-xs">รางวัล</span>
                  <div class="flex flex-wrap gap-2 mt-2">
                    <div v-for="(reward, index) in mission.rewards" :key="index" class="relative group">
                      <img
                        v-if="getItemImage(reward.itemName)"
                        :src="getItemImage(reward.itemName)"
                        :alt="reward.itemName"
                        class="w-10 h-10 object-contain rounded border border-gray-500"
                      />
                      <span v-else class="text-indigo-300 text-xs">{{ reward.itemName }}</span>
                      <span class="tooltip">{{ reward.itemName }}</span>
                    </div>
                  </div>
                </div>

                <!-- Claim -->
                <div class="flex justify-end mt-4">
                  <button
                    v-if="canClaimMission(mission)"
                    @click="claimMission(mission)"
                    :disabled="isProcessing"
                    class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-1.5 px-4 rounded-md transition-colors duration-200 border border-indigo-700 group relative"
                  >
                    <i class="fas fa-check text-xs"></i> รับรางวัล
                    <span class="tooltip">รับรางวัลภารกิจนี้</span>
                  </button>
                  <span v-else-if="isMissionClaimed(mission)" class="text-green-400 text-xs">สำเร็จแล้ว</span>
                  <span v-else-if="isProgressComplete(mission)" class="text-yellow-400 text-xs">ต้องอัพเกรดห้องถึงระดับ 5 เพื่อรับรางวัล</span>
                  <span v-else class="text-indigo-400 text-xs">ยังไม่สำเร็จ</span>
                </div>
              </div>
            </template>

            <!-- Completed -->
            <template v-else>
              <div
                v-for="mission in completedMain"
                :key="mission.missionId"
                class="flex flex-col p-4 bg-gray-700 rounded-md border border-gray-600 hover:border-indigo-500 transition-all duration-200"
              >
                <div class="flex justify-between items-center">
                  <div class="flex items-center gap-2">
                    <i class="fas fa-tasks text-indigo-400"></i>
                    <span class="font-medium text-indigo-200 text-base">{{ mission.name }}</span>
                  </div>
                  <span class="text-indigo-400 font-semibold text-sm">{{ missionProgress(mission) }}</span>
                </div>

                <div class="text-indigo-300 text-xs mt-2">{{ mission.description }}</div>

                <div class="w-full bg-gray-600 rounded-full h-2.5 mt-2 border border-gray-500">
                  <div
                    class="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                    :style="{ width: progressPercentage(mission) + '%' }"
                  ></div>
                </div>

                <div class="flex justify-end mt-4">
                  <span class="text-green-400 text-xs">สำเร็จแล้ว</span>
                </div>
              </div>
            </template>
          </template>

          <!-- Craft, Daily, and Weekly Tabs -->
          <template v-else>
            <div
              v-for="mission in getMissionsForTab(activeTab)"
              :key="mission.missionId"
              class="flex flex-col p-4 bg-gray-700 rounded-md border border-gray-600 hover:border-indigo-500 transition-all duration-200"
            >
              <div class="flex justify-between items-center">
                <div class="flex items-center gap-2">
                  <i class="fas fa-tasks text-indigo-400"></i>
                  <span class="font-medium text-indigo-200 text-base">{{ mission.name }}</span>

                  <!-- Small type icons -->
                  <i
                    v-if="getMissionType(mission) === 'daily'"
                    class="fas fa-clock text-yellow-400 text-xs group relative"
                  >
                    <span class="tooltip">ภารกิจรายวัน</span>
                  </i>
                  <i
                    v-if="getMissionType(mission) === 'weekly'"
                    class="fas fa-calendar-week text-yellow-400 text-xs group relative"
                  >
                    <span class="tooltip">ภารกิจรายสัปดาห์</span>
                  </i>
                  <i
                    v-if="getMissionType(mission) === 'main' && ['craftedItems', 'craftedItemsByName'].includes(mission.type)"
                    class="fas fa-hammer text-yellow-400 text-xs group relative"
                  >
                    <span class="tooltip">ภารกิจคราฟ</span>
                  </i>
                </div>
                <span class="text-indigo-400 font-semibold text-sm">{{ missionProgress(mission) }}</span>
              </div>

              <div class="text-indigo-300 text-xs mt-2">{{ mission.description }}</div>

              <div class="w-full bg-gray-600 rounded-full h-2.5 mt-2 border border-gray-500">
                <div
                  class="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                  :style="{ width: progressPercentage(mission) + '%' }"
                ></div>
              </div>

              <!-- Rewards -->
              <div class="mt-3">
                <span class="font-medium text-indigo-200 text-xs">รางวัล</span>
                <div class="flex flex-wrap gap-2 mt-2">
                  <div v-for="(reward, index) in mission.rewards" :key="index" class="relative group">
                    <img
                      v-if="getItemImage(reward.itemName)"
                      :src="getItemImage(reward.itemName)"
                      :alt="reward.itemName"
                      class="w-10 h-10 object-contain rounded border border-gray-500"
                    />
                    <span v-else class="text-indigo-300 text-xs">{{ reward.itemName }}</span>
                    <span class="tooltip">{{ reward.itemName }}</span>
                  </div>
                </div>
              </div>

              <!-- Claim -->
              <div class="flex justify-end mt-4">
                <button
                  v-if="canClaimMission(mission)"
                  @click="claimMission(mission)"
                  :disabled="isProcessing"
                  class="bg-indigo-600 hover:bg-indigo-700 text-white text-xs sm:text-sm py-1.5 px-4 rounded-md transition-colors duration-200 border border-indigo-700 group relative"
                >
                  <i class="fas fa-check text-xs"></i> รับรางวัล
                  <span class="tooltip">
                    รับรางวัล{{
                      getMissionType(mission) === 'daily' ? 'รายวัน' :
                      getMissionType(mission) === 'weekly' ? 'รายสัปดาห์' :
                      getMissionType(mission) === 'main' && ['craftedItems', 'craftedItemsByName'].includes(mission.type) ? 'คราฟ' : 'หลัก'
                    }}
                  </span>
                </button>
                <span v-else-if="isMissionClaimed(mission)" class="text-green-400 text-xs">สำเร็จแล้ว</span>
                <span v-else-if="isProgressComplete(mission)" class="text-yellow-400 text-xs">ต้องอัพเกรดห้องถึงระดับ 5 เพื่อรับรางวัล</span>
                <span v-else class="text-indigo-400 text-xs">ยังไม่สำเร็จ</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Footer -->
      <div class="border-t border-gray-700">
        <button
          @click="cancelModal"
          class="w-full py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors duration-200 border border-gray-500 text-sm group relative"
        >
          <i class="fas fa-times mr-1 text-sm group-hover:scale-110 transition-transform duration-200"></i> ปิด
          <span class="tooltip">ปิดหน้าต่างภารกิจ</span>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import getUser from '@/composables/getUser';
import { projectFunctions } from '@/firebase/config';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { ref, computed, watch, onMounted } from 'vue';
import gameItemsData from '@/data/gameItems.json';

export default {
  name: 'MissionModal',
  props: {
    missions: { type: Array, required: true },
    dailyMissions: { type: Array, required: true },
    weeklyMissions: { type: Array, required: true },
    characterStats: { type: Object, default: () => ({}) },
    dailyStats: { type: Object, default: () => ({}) },
    weeklyStats: { type: Object, default: () => ({}) },
    completedMissions: { type: Array, default: () => [] },
    claimedDailyMissions: { type: Array, default: () => [] },
    claimedWeeklyMissions: { type: Array, default: () => [] },
  },
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const isLoading = ref(false);
    const error = ref(null);
    const isProcessing = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const activeTab = ref('main');
    const activeMainSubTab = ref('incomplete');
    const tabs = [
      { id: 'claimable', name: 'สำเร็จแล้ว', tooltip: 'ดูภารกิจที่สำเร็จและพร้อมรับรางวัล' },
      { id: 'main', name: 'หลัก', tooltip: 'ดูภารกิจหลัก (ไม่รวมคราฟ)' },
      { id: 'craft', name: 'คราฟ', tooltip: 'ดูภารกิจคราฟไอเทม' },
      { id: 'daily', name: 'รายวัน', tooltip: 'ดูภารกิจรายวัน' },
      { id: 'weekly', name: 'รายสัปดาห์', tooltip: 'ดูภารกิจรายอาทิตย์' },
    ];



    onMounted(() => {
      isLoading.value = false;
    });

    const getMissionType = (mission) => {
      if (props.missions.some(m => m.missionId === mission.missionId)) return 'main';
      if (props.dailyMissions.some(m => m.missionId === mission.missionId)) return 'daily';
      if (props.weeklyMissions.some(m => m.missionId === mission.missionId)) return 'weekly';
      return null;
    };

    // Get missions for current tab
    const getMissionsForTab = computed(() => (tab) => {
      if (!user.value) return [];
      if (tab === 'claimable') {
        const mainClaimable = props.missions.filter(m => m.active && canClaimMission(m));
        const dailyClaimable = props.dailyMissions.filter(m => m.active && canClaimMission(m));
        const weeklyClaimable = props.weeklyMissions.filter(m => m.active && canClaimMission(m));
        return [...mainClaimable, ...dailyClaimable, ...weeklyClaimable].sort((a, b) => (a.order || 0) - (b.order || 0));
      } else if (tab === 'main') {
        return props.missions
          .filter(m => m.active && !['craftedItems', 'craftedItemsByName'].includes(m.type))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      } else if (tab === 'craft') {
        return props.missions
          .filter(m => m.active && ['craftedItems', 'craftedItemsByName'].includes(m.type))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      } else if (tab === 'daily') {
        return props.dailyMissions
          .filter(m => m.active)
          .sort((a, b) => getMissionPriority(a) - getMissionPriority(b) || (a.order || 0) - (b.order || 0));
      } else if (tab === 'weekly') {
        return props.weeklyMissions
          .filter(m => m.active)
          .sort((a, b) => getMissionPriority(a) - getMissionPriority(b) || (a.order || 0) - (b.order || 0));
      }
      return [];
    });

    const getMissionPriority = (mission) => {
      if (isMissionClaimed(mission)) return 2;
      if (canClaimMission(mission)) return 0;
      return 1;
    };

    // Calculate tab counts for claimable missions
    const tabClaimableCounts = computed(() => {
      if (!user.value) return { claimable: 0, main: 0, craft: 0, daily: 0, weekly: 0 };
      return {
        claimable: getMissionsForTab.value('claimable').length,
        main: props.missions.filter(m => canClaimMission(m) && !['craftedItems', 'craftedItemsByName'].includes(m.type)).length,
        craft: props.missions.filter(m => canClaimMission(m) && ['craftedItems', 'craftedItemsByName'].includes(m.type)).length,
        daily: props.dailyMissions.filter(m => canClaimMission(m)).length,
        weekly: props.weeklyMissions.filter(m => canClaimMission(m)).length,
      };
    });

    // For main tab groups (non-crafting missions)
    const incompleteMain = computed(() => {
      const mainMissions = getMissionsForTab.value('main');
      return mainMissions.filter(m => !isMissionClaimed(m)).sort((a, b) => (canClaimMission(b) - canClaimMission(a)) || (a.order || 0) - (b.order || 0));
    });

    const completedMain = computed(() => {
      const mainMissions = getMissionsForTab.value('main');
      return mainMissions.filter(m => isMissionClaimed(m)).sort((a, b) => (a.order || 0) - (b.order || 0));
    });

    // Calculate mission progress
    const missionProgress = (mission) => {
      if (!mission) return '0/0';
      const missionType = getMissionType(mission);
      if (!missionType) return '0/0';
      const stats = missionType === 'daily' ? props.dailyStats : missionType === 'weekly' ? props.weeklyStats : props.characterStats;
      let currentValue = getMissionValue(mission, stats, playerStore.characterData);
      const displayValue = Math.min(currentValue, mission.targetValue || 0);
      return `${displayValue}/${mission.targetValue || 0}`;
    };

    // Calculate progress percentage
    const progressPercentage = (mission) => {
      if (!mission) return 0;
      const missionType = getMissionType(mission);
      if (!missionType) return 0;
      const stats = missionType === 'daily' ? props.dailyStats : missionType === 'weekly' ? props.weeklyStats : props.characterStats;
      const currentValue = getMissionValue(mission, stats, playerStore.characterData);
      return mission.targetValue > 0 ? Math.min((currentValue / mission.targetValue) * 100, 100) : 0;
    };

    // Helper to get mission value
    const getMissionValue = (mission, stats, characterData) => {
      let currentValue = 0;
      switch (mission.type) {
        case 'explore_specific_map': currentValue = stats.explorationsByMap?.[mission.mapName] || 0; break;
        case 'zombiesKilled': currentValue = stats.zombiesKilled || 0; break;
        case 'zombiesKilled_specific': currentValue = stats.zombiesKilledByName?.[mission.zombieName] || 0; break;
        case 'criminalsKilled_specific': currentValue = stats.criminalsKilledByName?.[mission.criminalName] || 0; break;
        case 'decomposedItems': currentValue = stats.decomposedItems || 0; break;
        case 'decomposedItemsByName': currentValue = stats.decomposedItemsByName?.[mission.itemName] || 0; break;
        case 'skillsLearned': currentValue = stats.totalSkillsLearned || 0; break;
        case 'tattoosApplied': currentValue = stats.tattoosApplied || 0; break;
        case 'mapsExplored': currentValue = stats.mapsExplored || 0; break;
        case 'roomUpgrades': currentValue = stats.roomUpgrades || 0; break;
        case 'criminalsKilled': currentValue = stats.criminalsKilled || 0; break;
        case 'survivorsKilled': currentValue = stats.survivorsKilled || 0; break;
        case 'craftedItems': currentValue = stats.craftedItems || 0; break;
        case 'craftedItemsByName': currentValue = stats.craftedItemsByName?.[mission.itemName] || 0; break;
        case 'totalSilverFromItemSales':
        case 'silverFromItemSales': currentValue = stats.silverFromItemSales || 0; break;
        case 'equipmentUpgrades': currentValue = stats.equipmentUpgrades || 0; break;
        case 'mechanicalUpgrades': currentValue = stats.mechanicalUpgrades || 0; break;
        case 'raidBossParticipations': currentValue = stats.raidBossParticipations || 0; break;
        case 'level': currentValue = characterData.level || 1; break;
        default: break;
      }
      return currentValue;
    };

    // Check if mission progress is complete (progress >= target and not claimed)
    const isProgressComplete = (mission) => {
      const missionType = getMissionType(mission);
      if (!missionType) return false;
      const stats = missionType === 'daily' ? props.dailyStats : missionType === 'weekly' ? props.weeklyStats : props.characterStats;
      const currentValue = getMissionValue(mission, stats, playerStore.characterData);
      const isClaimed = isMissionClaimed(mission);
      return currentValue >= (mission.targetValue || 0) && !isClaimed;
    };

    // Check if mission is claimed/completed
    const isMissionClaimed = (mission) => {
      const missionType = getMissionType(mission);
      if (!missionType) return false;
      const claimedList = missionType === 'daily' ? props.claimedDailyMissions : missionType === 'weekly' ? props.claimedWeeklyMissions : props.completedMissions;
      return claimedList.includes(mission.missionId);
    };

    // Check if can claim mission
    const canClaimMission = (mission) => {
      const missionType = getMissionType(mission);
      if (!missionType) return false;
      const room = playerStore.characterData?.room || 0;
      return mission.active && user.value && isProgressComplete(mission) && ((missionType !== 'daily' && missionType !== 'weekly') || room >= 5);
    };

    // Set initial active tab based on claimable missions (moved after canClaimMission declaration)
    watch([isLoadingAuth, user], ([newIsLoading, newUser]) => {
      if (!newIsLoading && newUser) {
        const claimableMissions = [
          ...props.missions.filter(m => m.active && canClaimMission(m)),
          ...props.dailyMissions.filter(m => m.active && canClaimMission(m)),
          ...props.weeklyMissions.filter(m => m.active && canClaimMission(m)),
        ];
        activeTab.value = claimableMissions.length > 0 ? 'claimable' : 'main';
      }
    }, { immediate: true });

    // Claim reward
    const claimMission = async (mission) => {
      if (isProcessing.value || !user.value) return;
      isProcessing.value = true;
      successMessage.value = '';
      errorMessage.value = '';
      try {
        const missionType = getMissionType(mission);
        if (!missionType) throw new Error('ไม่พบประเภทภารกิจ');
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({
          action: 'missionActions',
          subAction: 'claimMission',
          missionId: mission.missionId,
          missionType,
        });
        if (result.data.message) {
          const itemNames = result.data?.rewardItemNames?.length ? result.data.rewardItemNames.join(', ') : 'ไม่มีไอเทม';
          successMessage.value = `รับรางวัล ${itemNames} สำเร็จ`;
        } else {
          throw new Error('การรับรางวัลล้มเหลว');
        }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการรับรางวัล';
        console.error('claimMission error:', err);
      } finally {
        isProcessing.value = false;
      }
    };

    // Get item image
    const getItemImage = (itemName) => {
      return gameItemsData[itemName]?.img || '';
    };

    // Close modal
    const cancelModal = () => emit('cancelModal');

    return {
      user, isLoadingAuth, isLoading, error, characterData: computed(() => playerStore.characterData),
      activeTab, tabs, activeMainSubTab,
      getMissionsForTab, tabClaimableCounts,
      incompleteMain, completedMain,
      missionProgress, progressPercentage, canClaimMission, claimMission,
      cancelModal, isProcessing, successMessage, errorMessage,
      isMissionClaimed, getItemImage, getMissionType, isProgressComplete
    };
  },
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }
@keyframes spin-pulse { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.animate-spin-pulse { animation: spin-pulse 1s linear infinite; }
@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
.animate-pulse { animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
.tooltip { visibility: hidden; position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%); background: #1f2937; color: #e0e7ff; padding: 0.5rem 1rem; border-radius: 0.375rem; font-size: 0.75rem; white-space: nowrap; z-index: 10; opacity: 0; transition: opacity 0.3s ease, visibility 0.3s ease; margin-bottom: 0.5rem; }
.group:hover .tooltip { visibility: visible; opacity: 1; }
@media (max-width: 639px) { .text-sm { font-size: 0.75rem; } .text-base { font-size: 0.875rem; } .p-4 { padding: 0.75rem; } .h-96 { max-height: 60vh; } }
button:disabled { opacity: 0.5; cursor: not-allowed; }
button:not(:disabled):hover { transform: scale(0.98); }
</style>
```
